import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { Disclosure, DisclosureKind } from "@/types/sms";

const DISCLOSURES_QUERY_KEY = ["sms-disclosures"];

/**
 * Reads the current (highest-version) disclosure text per kind, from
 * v_sms_current_disclosures, keyed by kind for direct lookup in the
 * Compliance card.
 */
export const useDisclosures = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<Record<DisclosureKind, Disclosure>>({
    queryKey: DISCLOSURES_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb.from("v_sms_current_disclosures").select("*");
      if (error) throw error;

      const rows = (data ?? []) as Disclosure[];
      return rows.reduce((acc, row) => {
        acc[row.kind] = row;
        return acc;
      }, {} as Record<DisclosureKind, Disclosure>);
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Disclosures are append-only (CP-4): saving inserts version+1 rather than
 * updating the current row in place, so past consent events keep citing the
 * version a customer actually saw.
 */
export const useSaveDisclosure = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kind, body }: { kind: DisclosureKind; body: string }): Promise<Disclosure> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data: currentRows, error: currentError } = await sb
        .from("v_sms_current_disclosures")
        .select("version")
        .eq("kind", kind)
        .maybeSingle();
      if (currentError) throw currentError;

      const nextVersion = ((currentRows as { version: number } | null)?.version ?? 0) + 1;

      const { data, error } = await sb
        .from("sms_disclosures")
        .insert({ kind, body, version: nextVersion })
        .select()
        .single();
      if (error) throw error;
      return data as Disclosure;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DISCLOSURES_QUERY_KEY });
    },
  });
};
